<script lang="ts" setup>
	import StringMethods from '~/utils/character'

	export type BreakdownAnalysis = {
		value: number | string
		label: string | string
		discount?: number
		discountCaption?: string
		tooltip?: string
		expandable?: boolean
		intro: {
			done: boolean
			value?: string | number
			label?: string
		}
		original_price?: string | number
		discount_name?: string
	}

	withDefaults(
		defineProps<{
			planName: string | null
			category: string
			summary?: {
				isEmpty: boolean
				note: string
				totalAmount: number | string
				[id: string]: unknown
			}
			exclude?: string[]
			skipHeader?: boolean
			breakdownAnalysis: BreakdownAnalysis
			loading?: boolean
		}>(),
		{
			exclude: () => [
				'isEmpty',
				'note',
				'totalAmount',
				'slot',
				'expandable',
				'discount_name',
				'original_price',
				'promo_code'
			]
		}
	)

	const { addProductsIconLine } = useUtils()
	const { stripUnderScore } = StringMethods()
	const { internationalNoFormat } = useCurrency()
	const sub = (a: number, b: number) => Number(a - b)
	defineEmits<{ (arg: 'view-more'): void }>()
</script>

<template>
	<div>
		<div
			v-if="!skipHeader"
			class="flex items-center justify-between gap-x-2 border-b border-success-500 px-5 py-3 capitalize"
		>
			<div class="flex items-center gap-x-2">
				<div
					class="inline-flex h-[28px] w-[28px] items-center justify-center rounded-full border border-success-100 bg-success-50"
				>
					<lazy-tools-icon
						:dir="addProductsIconLine(category || '', planName || '').dir"
						:icon="addProductsIconLine(category || '', planName || '').name"
						:height="13"
						:width="13"
						path-class="fill-success-500"
						:aria-label="addProductsIconLine(category || '', planName || '').name"
					/>
				</div>
				<span aria-label="Plan" class="font-semibold text-gray-700">
					{{ stripUnderScore(planName || '') }}
				</span>
			</div>
			<a-spin v-show="loading" size="small" spinning />
		</div>

		<div class="px-6 py-3 tracking-wide">
			<div class="flex flex-wrap items-end text-lg font-bold text-gray-600 sm:text-2xl">
				<div v-if="!breakdownAnalysis?.intro.done" class="flex w-full flex-wrap items-center gap-x-2">
					<h2 class="font-semibold text-gray-600">
						<div v-if="breakdownAnalysis?.original_price" class="flex items-center justify-between">
							<span class="block align-super text-xl text-gray-400 line-through">
								{{ internationalNoFormat(breakdownAnalysis.original_price, '₦') }}
							</span>
						</div>

						{{
							typeof breakdownAnalysis.intro.value === 'number'
								? internationalNoFormat(breakdownAnalysis.intro.value, '₦')
								: breakdownAnalysis.intro.value
						}}
					</h2>
					<span class="text-sm font-medium text-gray-250">
						{{ breakdownAnalysis.intro.label }}
					</span>
				</div>

				<div v-else class="w-full translate-y-1.5">
					<span
						v-if="
							breakdownAnalysis?.original_price &&
							sub(Number(breakdownAnalysis.original_price), Number(breakdownAnalysis.value))
						"
						class="block scale-y-95 overflow-hidden text-ellipsis pb-1 text-lg font-semibold leading-[28px] text-gray-400 line-through transition-all"
					>
						{{ internationalNoFormat(breakdownAnalysis.original_price, '₦') }}
					</span>
					<div class="flex w-full justify-between">
						<h2 class="overflow-hidden text-ellipsis font-semibold text-gray-600">
							{{
								typeof breakdownAnalysis.value === 'number'
									? internationalNoFormat(breakdownAnalysis.value, '₦')
									: breakdownAnalysis.value
							}}
						</h2>

						<button
							v-if="breakdownAnalysis.expandable"
							type="button"
							aria-label="view plan breakdown"
							@click="$emit('view-more')"
						>
							<lazy-tools-icon dir="arrows" icon="arrow-circle-right" />
						</button>
					</div>
					<span
						v-if="
							breakdownAnalysis.original_price &&
							sub(Number(breakdownAnalysis.original_price), Number(breakdownAnalysis.value))
						"
						class="overflow-hidden text-ellipsis text-sm font-normal"
					>
						{{
							internationalNoFormat(
								sub(Number(breakdownAnalysis.original_price), Number(breakdownAnalysis.value)),
								'₦'
							)
						}}
						saved on plan
					</span>
				</div>
			</div>

			<div v-if="summary && !summary?.isEmpty" class="custom-transition mt-3 border-t border-gray-300">
				<ul>
					<template v-for="(v, k) in summary" :key="k">
						<li
							v-if="!exclude.includes(String(k))"
							class="flex items-center justify-between pb-3 capitalize first:mt-5"
						>
							<span class="text-sm font-medium text-gray-500">
								{{ k }}
							</span>
							<span
								class="max-w-[100px] overflow-hidden text-ellipsis text-sm font-medium text-primary-2"
								:title="(v as string) || ''"
							>
								{{ String(v).replace(/_/g, ' ') }}
							</span>
						</li>
					</template>

					<li v-if="summary?.note" class="mt-5 flex gap-x-2">
						<lazy-tools-icon :height="18" :width="18" icon="warning-3" dir="fill" />
						<span
							class="overflow-hidden text-ellipsis text-sm font-semibold italic leading-[18px] text-gray-500"
							v-html="
								summary?.note.replace(
									/(₦[0-9,]+)/g,
									`<span class='font-medium text-primary-2 '>$1</span>`
								)
							"
						/>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>
